'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';

import Base from '../../layouts/Base';
import Section from '../../components/Section/Section';
import { t } from '../../i18n';
import StreamField from '../../components/StreamField';
import Button from '../../components/Button/Button';
import Grid from '../../components/Grid/Grid';

import s from './ReportArticlePage.module.scss';
import AuthorList from '../../components/AuthorList/AuthorList';
import AccordionFade from '../../components/AccordionFade/AccordionFade';
import Box from '../../components/Box/Box';
import DownloadFileItem from '../../components/DownloadFileItem/DownloadFileItem';
import TableOfContents from '../../components/TableOfContents/TableOfContents';
import classNames from 'classnames';
import Bookmark from '../../components/Bookmark/Bookmark';
import TagList from '../../components/TagList/TagList';
import { set } from 'lodash';
import { reportOriginUrl } from '../../utils/reportReturn';
import {
    ReportCommentsProvider,
    useReportComments,
} from './comments/ReportCommentsProvider';
import CommentCountButton from './comments/CommentCountButton';
import AddCommentButton from './comments/AddCommentButton';
import CommentPanel, { useCommentPanelLayout } from './comments/CommentPanel';
import BottomSheet from './comments/BottomSheet';
import markStyles from './comments/marks.module.scss';

const PdfEmbeddedViewer = dynamic(
    () => import('../../components/PdfEmbeddedViewer/PdfEmbeddedViewer'),
    { ssr: false }
);

const PdfFullscreenViewer = dynamic(
    () => import('../../components/PdfFullscreenViewer/PdfFullscreenViewer'),
    { ssr: false }
);

const ReportArticlePage = ({
    accordion = {},
    authors = [],
    publishedDate,
    updatedDate,
    articleTitle,
    subtitle,
    preamble,
    textBlocks,
    files = [],
    fullReportFile,
    dataSetFile,
    text,
    content,
    tags = [],
    countries = [],
    estimatedDelivery,
    pdfPreviews = [],
    rootUrl,
}) => {
    const pathname = usePathname();
    const [backUrl, setBackUrl] = useState(rootUrl);

    useEffect(() => {
        setBackUrl(reportOriginUrl(pathname) || rootUrl);
    }, [pathname, rootUrl]);

    const id = useId();
    const richtextRef = useRef(null);
    const articleContentRef = useRef(null);
    const scrollSyncLockRef = useRef(false);
    const commentPanelLayout = useCommentPanelLayout();
    const commentPanelInColumn = commentPanelLayout === 'column';
    const { commentableRef } = useReportComments();

    const [headings, setHeadings] = useState([]);
    const [activeHeading, setActiveHeading] = useState('Introduction');
    const [shouldScrollToHeading, setShouldScrollToHeading] = useState(false);
    const [fullscreenPdf, setFullscreenPdf] = useState(null);
    const [fullscreenIsLandscape, setFullscreenIsLandscape] = useState(false);
    const [fullscreenPage, setFullscreenPage] = useState(1);
    const [fullscreenTitle, setFullscreenTitle] = useState(1);

    const handleScrollSyncLockChange = useCallback((locked) => {
        scrollSyncLockRef.current = locked;
    }, []);

    const handleOpenFullscreen = useCallback(
        (file, page = 1, title, isLandscape = false) => {
            setFullscreenPdf(file);
            setFullscreenPage(page);
            setFullscreenTitle(title);
            setFullscreenIsLandscape(isLandscape);
        },
        []
    );

    const handleCloseFullscreen = useCallback(() => {
        setFullscreenPdf(null);
        setFullscreenPage(1);
    }, []);

    useEffect(() => {
        if (!richtextRef.current) return;

        const els = Array.from(
            richtextRef.current.querySelectorAll('h1, h2, h3, h4, h5')
        ).filter((el) => {
            const hide = el.getAttribute('data-hide-title-in-toc');
            return hide === null || hide === 'false';
        });

        const data = [];

        els.forEach((el) => {
            const rawText = el.textContent.trim();

            const slug = rawText
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/^-+|-+$/g, '');

            el.id = slug;
            if (!!rawText) {
                data.push({
                    text: rawText,
                    id: slug,
                    level: el.tagName.toLowerCase(),
                });
            }
        });

        setHeadings(data);
    }, [content]);

    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (!hash) return;

        if (hash === 'introduction') {
            setShouldScrollToHeading(true);
            handleScrollTo('Introduction', true);
        } else {
            const found = headings.find((h) => h.id === hash);
            if (found) {
                setShouldScrollToHeading(true);
                handleScrollTo(found.text, true);
            }
        }
    }, [headings]);

    const handleScrollTo = (headingText, shouldScroll = false) => {
        if (!shouldScroll) {
            setActiveHeading(headingText);
            return;
        }

        if (headingText === 'Introduction') {
            if (articleContentRef.current) {
                articleContentRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
            setActiveHeading(headingText);
            return;
        }

        const targetElement = Array.from(
            richtextRef.current.querySelectorAll('h1, h2, h3, h4, h5')
        ).find((el) => el.textContent === headingText);

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            setActiveHeading(headingText);
        }
    };

    const hasFiles = files.length > 0 || fullReportFile || dataSetFile;
    // The open comment panel takes the right column's place.
    const rightColumnHasTableOfContents =
        headings.length > 2 && !commentPanelInColumn;
    const rightColumnHasFiles = hasFiles && !commentPanelInColumn;
    const combinedTags = countries.concat(tags);
    const bookmarkCategories = [articleTitle, 'Reports'];

    return (
        <div className={s.Root}>
            <main className={s.Main}>
                <div className={s.Content}>
                    <Section noMarginBottom isMainSection>
                        <div className={s.LayoutWrapper}>
                            {headings.length > 2 && (
                                <div
                                    className={classNames(
                                        s.TableOfContents,
                                        s['TableOfContents--Mobile']
                                    )}>
                                    <TableOfContents
                                        headings={headings}
                                        onHeadingClick={(text) =>
                                            handleScrollTo(text, true)
                                        }
                                        onHeadingInView={(text) => {
                                            if (scrollSyncLockRef.current) {
                                                return;
                                            }
                                            setActiveHeading(text);
                                        }}
                                        onScrollSyncLockChange={
                                            handleScrollSyncLockChange
                                        }
                                        activeHeading={activeHeading}
                                        bookmarkTitle={articleTitle}
                                        bookmarkCategories={bookmarkCategories}
                                    />
                                </div>
                            )}

                            <div className={s.Left}>
                                <div className={s.ArticleTopBar}>
                                    <div className={s.ArticleTopBarLeft}>
                                        <TagList tags={combinedTags} />
                                    </div>
                                    <div className={s.ArticleTopBarRight}>
                                        <Bookmark
                                            url={pathname}
                                            modifiers={['Grey']}
                                            isSmall={false}
                                            categories={['Reports']}
                                            alternativeTitle={articleTitle}
                                        />
                                        <Button
                                            title={t(
                                                'reportArticlePage.backToReports'
                                            )}
                                            modifier="Secondary"
                                            url={backUrl}
                                        />
                                    </div>
                                </div>
                                <div className={s.Container}>
                                    <ArticleContent
                                        accordion={accordion}
                                        authors={authors}
                                        publishedDate={publishedDate}
                                        updatedDate={updatedDate}
                                        title={articleTitle}
                                        subtitle={subtitle}
                                        preamble={preamble}
                                        textBlocks={textBlocks}
                                        richtextRef={richtextRef}
                                        commentableRef={commentableRef}
                                        text={text}
                                        content={content}
                                        articleContentRef={articleContentRef}
                                        estimatedDelivery={estimatedDelivery}
                                        pdfPreviews={pdfPreviews}
                                        onOpenFullscreen={handleOpenFullscreen}
                                    />
                                </div>
                            </div>

                            <div className={s.Right}>
                                <div className={s.Container}>
                                    {commentPanelInColumn && <CommentPanel />}

                                    {rightColumnHasTableOfContents && (
                                        <div
                                            className={classNames(
                                                s.TableOfContents,
                                                s['TableOfContents--Desktop']
                                            )}>
                                            <span className={s.Label}>
                                                {t(
                                                    'reportArticlePage.tableOfContents'
                                                )}
                                            </span>
                                            <TableOfContents
                                                headings={headings}
                                                onHeadingClick={(text) =>
                                                    handleScrollTo(text, true)
                                                }
                                                onHeadingInView={(text) => {
                                                    if (
                                                        scrollSyncLockRef.current
                                                    ) {
                                                        return;
                                                    }
                                                    setActiveHeading(text);
                                                }}
                                                onScrollSyncLockChange={
                                                    handleScrollSyncLockChange
                                                }
                                                activeHeading={activeHeading}
                                                bookmarkTitle={articleTitle}
                                                bookmarkCategories={
                                                    bookmarkCategories
                                                }
                                            />
                                        </div>
                                    )}

                                    {rightColumnHasFiles && (
                                        <div className={s.RelatedFiles}>
                                            <span className={s.Label}>
                                                {t(
                                                    'reportArticlePage.relatedFiles'
                                                )}
                                            </span>
                                            <Box modifier="ArticlePage">
                                                {fullReportFile && (
                                                    <DownloadFileItem
                                                        {...fullReportFile}
                                                        buttonTitle={t(
                                                            'reportArticlePage.fullReport'
                                                        )}
                                                    />
                                                )}
                                                {dataSetFile && (
                                                    <DownloadFileItem
                                                        {...dataSetFile}
                                                        buttonTitle={t(
                                                            'reportArticlePage.dataSet'
                                                        )}
                                                    />
                                                )}
                                                {files.map((file, index) => (
                                                    <DownloadFileItem
                                                        key={index}
                                                        {...file.value}
                                                    />
                                                ))}
                                            </Box>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Section>
                </div>
            </main>
            <BottomSheet isOpen={commentPanelLayout === 'sheet'}>
                <CommentPanel />
            </BottomSheet>
            <AddCommentButton />
            <PdfFullscreenViewer
                open={!!fullscreenPdf}
                file={fullscreenPdf}
                onClose={handleCloseFullscreen}
                initialPage={fullscreenPage}
                isLandscape={fullscreenIsLandscape}
                title={fullscreenTitle}
            />
        </div>
    );
};

// Keeps the editor's block order: watchtower sections stay where they are,
// and runs of other blocks between them share one StreamField.
const groupContent = (content) =>
    content.reduce((groups, block) => {
        if (block.type === 'watchtower_section') {
            groups.push(block);
            return groups;
        }
        const last = groups[groups.length - 1];
        if (last && last.blocks) {
            last.blocks.push(block);
        } else {
            groups.push({ blocks: [block] });
        }
        return groups;
    }, []);

const ArticleContent = React.forwardRef(
    (
        {
            publishedDate,
            updatedDate,
            title,
            subtitle,
            authors,
            preamble,
            accordion,
            articleContentRef,
            richtextRef,
            commentableRef,
            content,
            text,
            estimatedDelivery,
            pdfPreviews,
            onOpenFullscreen,
        },
        ref
    ) => (
        <div
            className={s.ArticleContent}
            ref={articleContentRef}
            id="introduction">
            <div className={s.Byline}>
                <div className={s.Dates}>
                    <span className={s.Published}>
                        {`${t('reportArticlePage.published')} ${publishedDate}`}
                    </span>
                    {updatedDate && (
                        <span className={s.Updated}>
                            {`${t('reportArticlePage.updated')} ${updatedDate}`}
                        </span>
                    )}
                    {estimatedDelivery && (
                        <span className={s.Updated}>
                            {`${t('reportArticlePage.estimatedDelivery')} ${estimatedDelivery}`}
                        </span>
                    )}
                </div>
            </div>
            <h2 className={s.Title}>{title}</h2>
            {subtitle && <p className={s.Preamble}>{subtitle}</p>}
            <div className={s.Divider} />
            <div className={s.Authors}>
                {authors && authors.length > 0 && (
                    <AuthorList authors={authors} />
                )}
                <CommentCountButton />
            </div>
            {/* What readers can leave report comments on. */}
            <div ref={commentableRef} className={markStyles.Root}>
                {(preamble || text) && (
                    <div
                        className={s.Text}
                        dangerouslySetInnerHTML={{
                            __html: preamble ? preamble : text,
                        }}
                    />
                )}
                {!!accordion.title && !!accordion.content && (
                    <AccordionFade
                        {...accordion}
                        modifier="ArticlePage"
                        showThreeRows
                    />
                )}
                <div ref={richtextRef}>
                    {content &&
                        groupContent(content).map((group, index) => {
                            if (group.type === 'watchtower_section') {
                                const { grid_size, content: sectionContent } =
                                    group.value;
                                return (
                                    <Grid
                                        key={group.id || index}
                                        columns={parseInt(grid_size, 10) || 4}
                                        theme="minimal">
                                        <StreamField
                                            smallPadding
                                            noWrapper={true}
                                            items={sectionContent}
                                            componentModifier="NoPadding"
                                        />
                                    </Grid>
                                );
                            }
                            return (
                                <StreamField
                                    key={group.blocks[0].id || index}
                                    noWrapper={false}
                                    componentModifier="NoPadding"
                                    items={group.blocks}
                                />
                            );
                        })}
                </div>
            </div>
            {pdfPreviews && pdfPreviews.length > 0 && (
                <div className={s.PdfPreviews}>
                    {pdfPreviews.map((pdf, index) => (
                        <PdfEmbeddedViewer
                            key={pdf.uuid || index}
                            file={pdf.fileUrl}
                            title={pdf.title}
                            isLandscape={pdf.isLandscape}
                            onOpenFullscreen={(page) =>
                                onOpenFullscreen(
                                    pdf.fileUrl,
                                    page,
                                    pdf.title,
                                    pdf.isLandscape
                                )
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    )
);
// Around the whole page, so everything that shows comments shares one list.
const ReportArticlePageWithComments = (props) => (
    <ReportCommentsProvider pageId={props.id} readerName={props.user?.name}>
        <ReportArticlePage {...props} />
    </ReportCommentsProvider>
);

export const RawReportArticlePage = ReportArticlePageWithComments;
export default Base(ReportArticlePageWithComments);
